import { useQuery } from '@apollo/client';
import { GET_PARK_ZONE } from '@queries/getParkZone';
import {
  BaseText,
  Headline2,
  Headline3,
  Meta,
  theme as styleTheme,
} from 'aimo-ui';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import useFeedbackCount from 'hooks/useFeedbackCount';
import usePaymentDetails from 'hooks/usePaymentDetails';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/native';
import { Divider } from 'styles/CommonLayoutStyles';
import {
  ColumnWithMargins,
  FeedBackWrapper,
  RowSpaceBetween,
  StyledWrapper,
} from 'styles/ContainerStyles';
import {
  PaymentType,
  Query,
  QueryParkZoneArgs,
  StopChargingSessionResponse,
} from 'types/generatedSchemaTypes';
import { numberFormatter } from 'utils/commonUtils';
import { showFeatureFlag } from 'utils/featureFlagUtils';
import { getChargingSessionPaymentType } from 'utils/paymentUtils';
import { getDurationFromDates } from 'utils/timeUtils';
import Feedback from './Feedback';

const NotificationBox = styled.View`
  border-radius: 10px;
  background-color: ${styleTheme.colors.gray50};
  padding: 20px;
`;

const ChargingReportContent: React.FC<{
  sessionData?: StopChargingSessionResponse;
}> = ({ sessionData }) => {
  const { t } = useTranslation();
  const {
    flags: { show_feedback },
  } = useContext(FeatureFlagsContext);
  const {
    startTime,
    endTime,
    priceInCents,
    parkingZoneUid,
    licensePlate,
    cardId,
    energyInWatts,
    countryCode,
    corporateAccountUid,
    chargingSessionId,
    paymentType: chargingSessionPaymentType,
  } = sessionData || {};
  const { shouldShowFeedback } = useFeedbackCount(chargingSessionId ?? '');

  const { data, error, loading } = useQuery<Query, QueryParkZoneArgs>(
    GET_PARK_ZONE,
    {
      variables: { uid: parkingZoneUid || '' },
      skip: !parkingZoneUid,
    }
  );

  const paymentType = getChargingSessionPaymentType(
    cardId,
    corporateAccountUid,
    chargingSessionPaymentType as PaymentType
  );

  const paymentDetails = usePaymentDetails({
    transactionId: null, // For charging session, at this point there's no payment yet
    cardId,
    corporateAccountUid,
    totalAmount: priceInCents,
    paymentType,
  });

  if (!data || error || loading) {
    return <></>;
  }

  const duration = startTime
    ? getDurationFromDates(
        new Date(startTime),
        endTime ? new Date(endTime) : new Date()
      )
    : '';
  const energyConsumedKiloWatts = (energyInWatts || 0) / 1000;
  const energyUsed = `${
    countryCode
      ? numberFormatter(energyConsumedKiloWatts, countryCode)
      : energyConsumedKiloWatts
  } kWh`;

  const { name, commercialZoneView } = data.parkZone || {};
  return (
    <FeedBackWrapper
      resetPadding={true}
      borderRadius={50}
      backgroundColor="white">
      {shouldShowFeedback && showFeatureFlag(show_feedback) && (
        <>
          <Feedback sessionId={chargingSessionId ?? ''} intent="CHARGING" />
          <Divider size={10} />
        </>
      )}
      <FeedBackWrapper>
        <StyledWrapper mt={15} mb={15} center maxWidth={550}>
          <Headline3 testID="charging-report-sheet-title" center>
            {t('evCharging.report.sheetHeader')}
          </Headline3>
        </StyledWrapper>
        <StyledWrapper mb={0} center maxWidth={550}>
          <RowSpaceBetween>
            <Headline2 testID="charging-report-duration">{duration}</Headline2>
            <Headline2 testID="charging-report-total-price">
              {energyUsed}
            </Headline2>
          </RowSpaceBetween>
        </StyledWrapper>
        <StyledWrapper maxWidth={550} center mb={20}>
          <NotificationBox>
            <BaseText center>{t('evCharging.report.paymentInfo')}</BaseText>
          </NotificationBox>
        </StyledWrapper>
        <StyledWrapper mb={10} center maxWidth={550}>
          <RowSpaceBetween withBottomBorder>
            <ColumnWithMargins>
              <Meta color={styleTheme.colors.gray300}>
                {t('parking.receipt.location')}
              </Meta>
            </ColumnWithMargins>
            <ColumnWithMargins ml={15}>
              <Meta numberOfLines={1} testID="charging-report-location">
                <Meta color={styleTheme.colors.primary}>
                  {commercialZoneView?.zoneCode
                    ? commercialZoneView?.zoneCode + ' '
                    : ''}
                </Meta>
                {name}
              </Meta>
            </ColumnWithMargins>
          </RowSpaceBetween>
          <RowSpaceBetween withBottomBorder>
            <Meta color={styleTheme.colors.gray300}>
              {t('parking.receipt.car')}
            </Meta>
            <Meta testID="charging-report-license-plate">{licensePlate}</Meta>
          </RowSpaceBetween>
          <RowSpaceBetween>
            <Meta color={styleTheme.colors.gray300}>
              {t('parking.receipt.payment')}
            </Meta>
            <Meta testID="charging-report-payment-details">
              {paymentDetails?.paymentMethodName}
            </Meta>
          </RowSpaceBetween>
        </StyledWrapper>
      </FeedBackWrapper>
    </FeedBackWrapper>
  );
};

export default ChargingReportContent;
